import { HttpClient } from "@angular/common/http";
import { effect, inject, Injectable, signal } from "@angular/core";
import { Router } from "@angular/router";
import { environment } from "@environments/environment";
import { Property } from "@shared/models";
import { catchError, map, Observable, of, tap, throwError } from "rxjs";

@Injectable({
  providedIn: "root",
})
/**
 * Manages property (place) state for the authenticated user.
 *
 * Responsibilities include fetching properties from the API, exposing loading and
 * selection state via Angular signals, and synchronizing selected property from
 * route segments in the form of `property/:id`.
 */
export class PropertyService {
  private http = inject(HttpClient);
  private router = inject(Router);

  /** Internal mutable store of all fetched properties. */
  private propertiesSignal = signal<Property[]>([]);

  /** Internal mutable loading flag for property fetch operations. */
  private loadingSignal = signal<boolean>(false);

  /**
   * Reserved internal selection signal.
   *
   * Note: current public API writes to `selectedProperty`.
   */
  private selectedPropertySignal = signal<Property | null>(null);

  /** Read-only list of properties for template and component consumers. */
  readonly properties = this.propertiesSignal.asReadonly();

  /** Read-only loading state that is true while `getProperties()` is in progress. */
  readonly loading = this.loadingSignal.asReadonly();

  /** Currently selected property in application context. */
  readonly selectedProperty = signal<Property | null>(null);

  constructor() {
    effect(() => {
      const propertyId = this.getPropertyIdFromUrl();

      if (propertyId) {
        const found = this.properties().find(p => p.placeId === propertyId);
        if (found && found !== this.selectedProperty()) {
          this.selectedProperty.set(found);
        }
      }
    });
  }

  /**
   * Returns the authenticated user's properties.
   *
   * Uses cached properties when already loaded. Otherwise, fetches from API,
   * maps raw objects into `Property` instances, updates local state, and
   * auto-selects the property when only one is available.
   *
   * @returns Observable stream of properties.
   */
  getProperties(): Observable<Property[]> {
    if (this.properties().length > 0) {
      return of(this.properties());
    }
    this.loadingSignal.set(true);

    return this.http
      .get<Property[]>(`${environment.apiUrl}/v1/users/me/places/search`)
      .pipe(
        map((properties) => properties.map((property) => new Property(property))),
        tap((properties) => {
          this.propertiesSignal.set(properties);
          this.loadingSignal.set(false);

          // Auto-select if only one property
          if (properties.length === 1) {
            this.selectProperty(properties[0]);
          }
        }),
        catchError((error) => {
          this.loadingSignal.set(false);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Sets the currently selected property.
   *
   * @param property Property to set as selected.
   */
  selectProperty(property: Property): void {
    this.selectedProperty.set(property);
  }

  /** Clears the current property selection. */
  clearSelection(): void {
    this.selectedProperty.set(null);
  }

  /**
   * Finds a cached property by `placeId`.
   *
   * @param id Property place identifier.
   * @returns Matching property when found; otherwise `undefined`.
   */
  getPropertyById(id: number): Property | undefined {
    const p = this.propertiesSignal().find((p) => p.placeId === id);
    return p;
  }

  /** Clears cached properties and resets selected property state. */
  clearProperties(): void {
    this.propertiesSignal.set([]);
    this.selectedProperty.set(null);
  }

  /**
   * Extracts `property/:id` from the current URL.
   *
   * @returns Parsed property id from route when present; otherwise `null`.
   */
  private getPropertyIdFromUrl(): number | null {
    const urlTree = this.router.parseUrl(this.router.url);
    const segments = urlTree.root.children['primary']?.segments;

    if (segments) {
      const propertyIndex = segments.findIndex(s => s.path === 'property');
      if (propertyIndex !== -1 && segments[propertyIndex + 1]) {
        return Number(segments[propertyIndex + 1].path);
      }
    }
    return null;
  }
}
